import type {
  Connector,
  ConnectorStandard,
  EvseStatus,
  OcpiLocation,
  PowerType,
} from "../types/ocpi";

/**
 * Connector kW from voltage × amperage. AC 3-phase uses √3 factor.
 * Falls back to max_electric_power (watts) when present.
 */
export function connectorKw(c: Connector): number {
  if (c.max_electric_power && c.max_electric_power > 0) {
    return Math.round(c.max_electric_power / 1000);
  }
  const v = Number(c.max_voltage) || 0;
  const a = Number(c.max_amperage) || 0;
  if (!v || !a) return 0;
  if (c.power_type === "AC_3_PHASE") return Math.round((v * a * Math.sqrt(3)) / 1000);
  if (c.power_type === "AC_2_PHASE") return Math.round((v * a) / 1000);
  // AC_1_PHASE & DC
  return Math.round((v * a) / 1000);
}

export function locationMaxKw(loc: OcpiLocation): number {
  let max = 0;
  for (const e of loc.evses) for (const c of e.connectors) {
    const kw = connectorKw(c);
    if (kw > max) max = kw;
  }
  return max;
}

export function isDc(c: Connector): boolean {
  return String(c.power_type).startsWith("DC");
}

export function locationHasDc(loc: OcpiLocation): boolean {
  return loc.evses.some((e) => e.connectors.some(isDc));
}

export function locationHasAc(loc: OcpiLocation): boolean {
  return loc.evses.some((e) => e.connectors.some((c) => !isDc(c)));
}

export function totalConnectors(loc: OcpiLocation): number {
  return loc.evses.reduce((sum, e) => sum + e.connectors.length, 0);
}

/** Tier used for marker color and tariff lookup. */
export type PowerTier = "AC" | "DC_FAST" | "DC_ULTRA";

export function powerTier(kw: number, dc: boolean): PowerTier {
  if (!dc) return "AC";
  if (kw >= 150) return "DC_ULTRA";
  return "DC_FAST";
}

export function locationTier(loc: OcpiLocation): PowerTier {
  let bestKw = 0;
  let bestDc = false;
  for (const e of loc.evses) for (const c of e.connectors) {
    const kw = connectorKw(c);
    if (kw > bestKw || (kw === bestKw && isDc(c) && !bestDc)) {
      bestKw = kw;
      bestDc = isDc(c);
    }
  }
  return powerTier(bestKw, bestDc);
}

/** Aggregate EVSE/location status: best-of in priority order. */
export function aggregateStatus(loc: OcpiLocation): EvseStatus {
  const order: EvseStatus[] = ["AVAILABLE", "CHARGING", "RESERVED", "UNKNOWN", "OUTOFORDER", "INOPERATIVE", "BLOCKED", "PLANNED", "REMOVED"];
  const set = new Set(loc.evses.map((e) => e.status));
  for (const s of order) if (set.has(s)) return s;
  return "UNKNOWN";
}

const STANDARD_LABEL: Record<string, string> = {
  IEC_62196_T2: "Type 2 (Mennekes)",
  IEC_62196_T2_COMBO: "CCS2 (Combo)",
  IEC_62196_T1: "Type 1 (J1772)",
  IEC_62196_T1_COMBO: "CCS1 (Combo)",
  CHADEMO: "CHAdeMO",
  TESLA_S: "Tesla",
  TESLA_R: "Tesla",
  DOMESTIC_F: "Schuko",
};
export function standardLabel(s: ConnectorStandard): string {
  return STANDARD_LABEL[s] ?? s.replace(/_/g, " ");
}

const POWER_TYPE_LABEL_TR: Record<string, string> = {
  AC_1_PHASE: "AC 1 Faz",
  AC_2_PHASE: "AC 2 Faz",
  AC_3_PHASE: "AC 3 Faz",
  DC: "DC",
};
const POWER_TYPE_LABEL_EN: Record<string, string> = {
  AC_1_PHASE: "AC 1-phase",
  AC_2_PHASE: "AC 2-phase",
  AC_3_PHASE: "AC 3-phase",
  DC: "DC",
};
export function powerTypeLabel(p: PowerType, lang: "tr" | "en"): string {
  const map = lang === "tr" ? POWER_TYPE_LABEL_TR : POWER_TYPE_LABEL_EN;
  return map[p] ?? p;
}

export function formatKw(kw: number): string {
  if (!kw) return "—";
  return `${kw.toLocaleString(undefined, { maximumFractionDigits: 0 })} kW`;
}

export function formatPrice(price: number, currency: string, lang: "tr" | "en"): string {
  const locale = lang === "tr" ? "tr-TR" : "en-US";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }).format(price);
  } catch {
    return `${price.toFixed(2)} ${currency}`;
  }
}

export function haversineKm(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

export function locCoord(loc: OcpiLocation): { lat: number; lng: number } {
  return {
    lat: parseFloat(loc.coordinates.latitude),
    lng: parseFloat(loc.coordinates.longitude),
  };
}
